import { Component } from '@theme/component';
import { CartAddEvent } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';

/**
 * <aromely-car-pdp> — the ARO CAR page (draft theme). Picks a colour (a real variant of this
 * product), then "diffuser only" or "diffuser + fragrance": 1, 2 or 3 of the 20 ml bottles the
 * section renders as cards (same set logic as the home-diffuser pages, one bottle size). Every
 * line is a real variant: the diffuser plus one line per chosen bottle, each at the quantity of
 * sets. Prices come from the data island and the cards; nothing is discounted or invented.
 */
class AromelyCarPdp extends Component {
  #ac = null;
  #sticky = null;
  #stickyIO = null;
  #drawerMO = null;
  #variants = [];
  #v = null;
  #maxScents = 3;
  #state = { mode: 'solo', qty: 1, scents: new Map(), pickerOpen: false, busy: false };

  connectedCallback() {
    super.connectedCallback();
    // A new controller per connection (the theme editor re-connects elements).
    this.#ac = new AbortController();
    try { this.#variants = JSON.parse(this.dataset.variants || '[]'); } catch { this.#variants = []; }
    const wanted = new URL(location.href).searchParams.get('variant');
    this.#v = this.#variants.find((x) => String(x.id) === wanted)
      || this.#variants.find((x) => String(x.id) === this.dataset.variantId)
      || this.#variants.find((x) => x.available) || this.#variants[0] || null;
    const root = this.closest('[data-acp-root]');
    this.setupSticky(root);
    // "Build your set" buttons elsewhere on the page
    root?.querySelectorAll('[data-acp-choose-set]').forEach((b) => b.addEventListener('click', () => this.chooseSet(), { signal: this.#ac.signal }));
    // fragrance band: one-tap "Add to cart" for a single 20 ml bottle (opens the drawer)
    root?.querySelectorAll('[data-acp-quick-add]').forEach((b) => b.addEventListener('click', () => this.quickAdd(b), { signal: this.#ac.signal }));
    this.#renderSetFromPrice();
    this.render();
    // the gallery mounts independently; filter it to the starting colour once it exists
    this.#gallery()?.filter?.(this.#v?.color || '');
    customElements.whenDefined('aromely-diffuser-gallery').then(() => this.#gallery()?.filter?.(this.#v?.color || ''));
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#ac?.abort();
    this.#stickyIO?.disconnect();
    this.#drawerMO?.disconnect();
    this.setStickyVisible(false);
  }

  money(cents) {
    return formatMoney(cents, this.dataset.moneyFormat || '${{amount}}', this.dataset.currency || 'USD');
  }

  #root() { return this.closest('[data-acp-root]'); }
  /** Elements outside this component (price head, in-the-box colour) — not in this.refs. */
  #outer(name) { return this.#root()?.querySelector(`[ref="${name}"]`) || null; }
  #gallery() { return this.#root()?.querySelector('aromely-diffuser-gallery') || null; }

  /* ─── colour ─── */
  pickColor(event) {
    const btn = event.target.closest('[data-variant-id]');
    if (!btn || btn.getAttribute('aria-disabled') === 'true') return;
    const next = this.#variants.find((x) => String(x.id) === btn.dataset.variantId);
    if (!next || next === this.#v) return;
    this.#v = next;
    try {
      const url = new URL(location.href);
      url.searchParams.set('variant', String(next.id));
      history.replaceState(history.state, '', url);
    } catch { /* ignore */ }
    this.#gallery()?.filter?.(next.color || '');
    this.error('');
    this.render();
  }

  /* ─── solo / set ─── */
  setMode(mode) {
    if (mode !== 'solo' && mode !== 'set') return;
    if (this.#state.mode === mode) return;
    this.#state.mode = mode;
    if (mode === 'set') this.#state.pickerOpen = true; // never collapses on its own
    this.error('');
    this.render();
  }

  chooseSet() {
    this.#state.mode = 'set';
    this.#state.pickerOpen = true;
    this.render();
    (this.refs.picker || this).scrollIntoView({ behavior: 'smooth', block: 'start' });
    this.refs.picker?.querySelector('.acp-pick')?.focus({ preventScroll: true });
  }

  #scentCard(pid) { return this.querySelector(`[data-scent][data-pid="${pid}"]`); }
  #scentVariant(pid) {
    const el = this.#scentCard(pid);
    const vid = el?.dataset.vid;
    const price = Number(el?.dataset.price);
    if (!vid || !Number.isFinite(price)) return null;
    return { vid, price };
  }

  toggleScent(event) {
    const card = event.target.closest('[data-scent]');
    const pid = card?.dataset.pid;
    if (!pid || card.getAttribute('aria-disabled') === 'true') return;
    const { scents } = this.#state;
    if (scents.has(pid)) {
      scents.delete(pid);
    } else {
      if (scents.size >= this.#maxScents) {
        this.#note(`You can pick up to ${this.#maxScents} fragrances. Remove one to swap it.`, false);
        return;
      }
      scents.set(pid, { pid, name: card.dataset.name, url: card.dataset.url });
    }
    this.render();
  }

  removeScent(pid) {
    this.#state.scents.delete(pid);
    this.render();
  }

  pickerDone() {
    if (this.#setIncomplete) { this.#note('Choose at least one fragrance to complete the set.', false); return; }
    this.#state.pickerOpen = false;
    this.render();
    this.refs.addBtn?.focus({ preventScroll: true });
  }

  pickerEdit() {
    this.#state.pickerOpen = true;
    this.render();
    this.refs.picker?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  #note(text, ok) {
    const el = this.refs.pickNote;
    if (!el) return;
    el.textContent = text;
    el.dataset.ok = ok ? 'true' : 'false';
  }

  qtyUp() { if (this.#state.qty < 10) { this.#state.qty += 1; this.render(); } }
  qtyDown() { if (this.#state.qty > 1) { this.#state.qty -= 1; this.render(); } }

  /* ─── pricing ─── */
  get #setIncomplete() { return this.#state.mode === 'set' && this.#state.scents.size === 0; }
  /** October Sale percentage while the promotion is live in the browser (from the promo state), else 0. */
  get #promoPct() {
    const promo = window.AromelyPromo;
    if (!promo || typeof promo.active !== 'function' || !promo.active()) return 0;
    let pct = Number(promo.state?.pct);
    if (!Number.isFinite(pct) || pct <= 0) {
      try { pct = Number(JSON.parse(document.getElementById('aromely-promo-state')?.textContent || '{}').pct); } catch { pct = 0; }
    }
    return Number.isFinite(pct) && pct > 0 ? pct : 0;
  }
  /** Sale price of one unit in cents; Shopify truncates the OCTSALE discount per unit. */
  #saleCents(cents) {
    const pct = this.#promoPct;
    return pct > 0 ? cents - Math.floor((cents * pct) / 100) : cents;
  }
  /** Repaint the shared price line: sale price, struck full price and the sale badge (mirrors aromely-diffuser-mvp). */
  #paintSaleLine(priceEl, compareEl, badgeEl, fullCents, suffix = '', saleOverride) {
    const pct = this.#promoPct;
    const sale = saleOverride ?? this.#saleCents(fullCents);
    const show = pct > 0 && fullCents > sale;
    if (priceEl) priceEl.textContent = `${this.money(sale)}${suffix}`;
    if (compareEl) { compareEl.hidden = !show; compareEl.textContent = show ? this.money(fullCents) : ''; }
    if (badgeEl) {
      const wrap = priceEl?.closest('[data-promo-label]');
      const prefix = wrap?.dataset.promoLabel || `${window.AromelyPromo?.state?.name || 'Sale'} · −`;
      badgeEl.textContent = show ? `${prefix}${pct}%` : '';
      badgeEl.classList.toggle('hidden', !show);
      badgeEl.classList.toggle('inline-flex', show);
    }
    return sale;
  }
  /** One unit of the current configuration: the diffuser [+ the chosen bottles]. */
  #unitCents() {
    let total = this.#v?.price || 0;
    if (this.#state.mode === 'set') {
      for (const s of this.#state.scents.values()) {
        const sv = this.#scentVariant(s.pid);
        if (sv) total += sv.price;
      }
    }
    return total;
  }
  /** Sum discounted real product units before applying the number of sets. */
  #saleUnitCents() {
    let total = this.#saleCents(this.#v?.price || 0);
    if (this.#state.mode === 'set') {
      for (const scent of this.#state.scents.values()) {
        const variant = this.#scentVariant(scent.pid);
        if (variant) total += this.#saleCents(variant.price);
      }
    }
    return total;
  }
  #renderSetFromPrice() {
    const el = this.refs.setFromPrice;
    if (!el) return;
    let cheapest = Infinity;
    this.querySelectorAll('[data-scent]').forEach((c) => {
      const p = Number(c.dataset.price);
      if (c.dataset.vid && Number.isFinite(p) && p < cheapest) cheapest = p;
    });
    el.textContent = Number.isFinite(cheapest) && this.#v ? `from ${this.money(this.#saleCents(this.#v.price) + this.#saleCents(cheapest))}` : '';
  }

  /* ─── cart ─── */
  async fetchCart() {
    try {
      return await fetch(`${Theme.routes.cart_url || '/cart'}.js`, { headers: { Accept: 'application/json' } }).then((x) => x.json());
    } catch { return null; }
  }

  async addToCart() {
    const v = this.#v;
    if (this.#state.busy || !v || !v.available) return;
    if (this.#setIncomplete) { this.chooseSet(); return; }
    const qty = this.#state.qty;
    const items = [{ id: Number(v.id), quantity: qty }];
    if (this.#state.mode === 'set') {
      for (const s of this.#state.scents.values()) {
        const sv = this.#scentVariant(s.pid);
        if (!sv) { this.error(`${s.name} isn't available any more. Remove it or pick another.`); return; }
        items.push({ id: Number(sv.vid), quantity: qty });
      }
    }
    this.#state.busy = true;
    this.error('');
    this.render();
    // Baseline from the header badge (hidden bubble = empty cart): no pre-flight request.
    const before = this.#badgeCount();
    try {
      const res = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.status) {
        const text = typeof data?.description === 'string' ? data.description
          : typeof data?.message === 'string' && data.message !== 'Cart Error' ? data.message : '';
        // A stock error can leave part of a multi-line request in the cart — say what actually happened.
        await this.#explainFailure(`Couldn’t add this to your cart: ${text || 'please try again'}.`, before);
        return;
      }
      // No second request: the drawer fetches its own section on this event and syncs the badge.
      this.dispatchEvent(new CartAddEvent(data, this.id, {
        source: 'aromely-car-pdp',
        productId: this.dataset.productId,
        variantId: String(v.id),
      }));
      try { window.clarity?.('set', 'car_add', this.#state.mode === 'set' ? `set_${this.#state.scents.size}` : 'solo'); } catch { /* ignore */ }
    } catch (err) {
      console.error('[aromely-car-pdp] add to cart failed', err);
      await this.#explainFailure('We couldn’t confirm this with the store — check your connection.', before);
    } finally {
      this.#state.busy = false;
      this.render();
    }
  }

  /** Screen-reader line for the quick-add band (the button text alone is not announced). */
  #quickStatus(text) {
    const el = this.#root()?.querySelector('[data-acp-quick-status]');
    if (!el) return;
    el.textContent = '';
    setTimeout(() => { el.textContent = text; }, 50);
  }

  /** Adds one 20 ml bottle from the fragrance band; the button reports its own state. */
  async quickAdd(btn) {
    const vid = Number(btn?.dataset.acpQuickAdd);
    if (!vid || btn.disabled || btn.dataset.state === 'loading') return;
    const label = btn.innerHTML;
    btn.dataset.state = 'loading';
    btn.setAttribute('aria-busy', 'true');
    try {
      const res = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: vid, quantity: 1 }] }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.status) {
        const text = typeof data?.description === 'string' ? data.description : '';
        btn.dataset.state = '';
        btn.textContent = text ? `Couldn’t add — ${text}` : 'Couldn’t add — try again';
        this.#quickStatus(`${btn.dataset.name || 'Fragrance'} was not added${text ? ` — ${text}` : ''}.`);
        setTimeout(() => { btn.innerHTML = label; }, 3000);
        return;
      }
      this.dispatchEvent(new CartAddEvent(data, this.id, { source: 'aromely-car-pdp', productId: this.dataset.productId, variantId: String(vid) }));
      try { window.clarity?.('set', 'car_add', 'bottle_only'); } catch { /* ignore */ }
      btn.dataset.state = 'added';
      btn.textContent = 'Added ✓';
      this.#quickStatus(`${btn.dataset.name || 'Fragrance'}, 20 ml, added to cart.`);
      setTimeout(() => { btn.dataset.state = ''; btn.innerHTML = label; }, 2200);
    } catch (err) {
      console.error('[aromely-car-pdp] quick add failed', err);
      btn.dataset.state = '';
      btn.textContent = 'Couldn’t add — check your connection';
      this.#quickStatus(`${btn.dataset.name || 'Fragrance'} was not added — check your connection.`);
      setTimeout(() => { btn.innerHTML = label; }, 3000);
    } finally {
      btn.removeAttribute('aria-busy');
    }
  }

  /** Header badge count; NaN when there is no badge to read. */
  #badgeCount() {
    const bubble = document.querySelector('[data-aromely-cart-bubble]');
    if (!bubble) return undefined;
    if (bubble.classList.contains('hidden')) return 0;
    const n = parseInt(bubble.querySelector('[data-aromely-cart-count]')?.textContent || '', 10);
    return Number.isNaN(n) ? undefined : n;
  }

  async #explainFailure(lead, before) {
    const cart = await this.fetchCart();
    if (cart && before !== undefined && cart.item_count !== before) {
      this.dispatchEvent(new CartAddEvent({ items: [] }, this.id, { source: 'aromely-car-pdp', itemCount: cart.item_count }));
      this.error(`${lead} Part of it did reach your cart — please check the cart before trying again.`);
    } else if (cart && before !== undefined) {
      this.error(`${lead} Nothing was added.`);
    } else {
      this.error(`${lead} Please check your cart before trying again.`);
    }
  }

  error(text) {
    const el = this.refs.error;
    if (!el) return;
    el.hidden = !text;
    el.textContent = text || '';
  }

  /* ─── sticky bar (mobile) ─── */
  setupSticky(root) {
    this.#sticky = root?.querySelector('[data-acp-sticky]') || null;
    if (!this.#sticky || !this.refs.addBtn) return;
    this.#sticky.querySelector('[data-acp-sticky-btn]')?.addEventListener('click', () => this.addToCart(), { signal: this.#ac.signal });
    let atcVisible = true;
    let drawerOpen = false;
    const update = () => this.setStickyVisible(!atcVisible && !drawerOpen);
    const measure = () => { atcVisible = this.refs.addBtn.getBoundingClientRect().bottom > 0; };
    this.#stickyIO = new IntersectionObserver(() => { measure(); update(); }, { threshold: 0 });
    this.#stickyIO.observe(this.refs.addBtn);
    window.addEventListener('scroll', () => { measure(); update(); }, { passive: true, signal: this.#ac.signal });
    const dialog = document.querySelector('aromely-cart-drawer-component dialog, cart-drawer-component dialog');
    if (dialog) {
      drawerOpen = dialog.open;
      this.#drawerMO = new MutationObserver(() => { drawerOpen = dialog.open; update(); });
      this.#drawerMO.observe(dialog, { attributes: true, attributeFilter: ['open'] });
    }
    window.addEventListener('resize', update, { signal: this.#ac.signal });
  }

  setStickyVisible(show) {
    if (!this.#sticky) return;
    this.#sticky.hidden = !show;
    const root = this.#root();
    const h = show ? `${this.#sticky.offsetHeight}px` : '0px';
    if (root) root.style.setProperty('--sticky-h', h);
    document.documentElement.style.setProperty('--aromely-sticky-h', h); // body padding (C12)
  }

  /* ─── paint ─── */
  render() {
    const r = this.refs;
    const st = this.#state;
    const v = this.#v;
    const name = this.dataset.productName || 'ARO CAR';
    const colour = v?.color ? ` (${v.title})` : '';
    const n = st.scents.size;
    const incomplete = this.#setIncomplete;
    const unit = this.#unitCents();
    const saleUnit = this.#saleUnitCents();
    // October Sale: every repainted figure is the sale price while the promotion is live (full price otherwise).
    const saleTotal = saleUnit * st.qty;
    const plural = (k, w) => `${k} ${w}${k === 1 ? '' : 's'}`;
    const names = [...st.scents.values()].map((s) => s.name);

    for (const s of r.swatches || []) s.setAttribute('aria-pressed', String(v && String(v.id) === s.dataset.variantId));
    if (r.colorName && v) r.colorName.textContent = v.title || '';
    const boxColor = this.#outer('boxColor');
    if (boxColor && v) boxColor.textContent = v.title || '';

    if (r.soloPrice && v) r.soloPrice.textContent = this.money(this.#saleCents(v.price));
    this.#renderSetFromPrice();
    if (r.modeSolo) r.modeSolo.setAttribute('aria-pressed', String(st.mode === 'solo'));
    if (r.modeSet) r.modeSet.setAttribute('aria-pressed', String(st.mode === 'set'));

    // picker + cards
    if (r.picker) r.picker.hidden = !(st.mode === 'set' && st.pickerOpen);
    if (r.setSummary) r.setSummary.hidden = !(st.mode === 'set' && !st.pickerOpen && n > 0);
    this.querySelectorAll('[data-scent]').forEach((card) => card.setAttribute('aria-pressed', st.scents.has(card.dataset.pid) ? 'true' : 'false'));
    if (r.pickCount) r.pickCount.textContent = `${n} of ${this.#maxScents} chosen`;
    if (r.pickList) {
      r.pickList.replaceChildren(...[...st.scents.values()].map((s) => {
        const sv = this.#scentVariant(s.pid);
        const li = document.createElement('li');
        const span = document.createElement('span');
        const b = document.createElement('b'); b.textContent = s.name;
        const em = document.createElement('em'); em.textContent = ` · 20 ml${sv ? ` · ${this.money(this.#saleCents(sv.price))}` : ''}`;
        span.append(b, em);
        const btn = document.createElement('button');
        btn.type = 'button'; btn.textContent = 'Remove';
        btn.setAttribute('aria-label', `Remove ${s.name}`);
        btn.addEventListener('click', () => this.removeScent(s.pid));
        li.append(span, btn);
        return li;
      }));
    }
    if (r.pickDone) r.pickDone.disabled = n === 0;
    if (n === 0) this.#note('Choose at least one fragrance to complete the set.', false);
    else this.#note(n < this.#maxScents ? `Add up to ${this.#maxScents - n} more, or tap Done.` : 'That’s the full set — tap Done.', true);
    if (r.setSummaryText) r.setSummaryText.textContent = names.map((x) => `${x} (20 ml)`).join(', ');

    // price head — sale price while the October Sale is live, full price struck beside it.
    // The section renders the line through aromely-price with refs salePrice/saleCompare/saleBadge
    // (legacy `price` ref kept as a fallback for older markup).
    const price = this.#outer('salePrice') || this.#outer('price');
    const priceLabel = this.#outer('priceLabel');
    if (price && v) this.#paintSaleLine(price, this.#outer('saleCompare'), this.#outer('saleBadge'), unit, incomplete ? ' + fragrance' : '', saleUnit);
    if (priceLabel) {
      priceLabel.textContent = st.mode === 'solo' ? 'diffuser only'
        : incomplete ? 'diffuser + fragrance — choose 1 to 3'
        : `diffuser + ${plural(n, 'fragrance')}`;
    }

    // quantity + summary
    if (r.qtyWrap) r.qtyWrap.setAttribute('aria-label', st.mode === 'set' ? 'Number of sets' : 'Quantity');
    if (r.qtyValue) r.qtyValue.value = String(st.qty);
    if (r.qtyMinus) r.qtyMinus.disabled = st.qty <= 1;
    if (r.qtyPlus) r.qtyPlus.disabled = st.qty >= 10;
    let summary;
    if (st.mode === 'solo') summary = `${plural(st.qty, `${name} diffuser${colour}`)}. Fragrance oil sold separately.`;
    else if (incomplete) summary = `${plural(st.qty, 'set')}: ${plural(st.qty, `${name} diffuser${colour}`)} + fragrance still to choose.`;
    else summary = `${plural(st.qty, 'set')}: ${plural(st.qty, `diffuser${colour}`)} + ${plural(st.qty * n, 'bottle')} of 20 ml (${names.join(', ')}).`;
    if (r.summary) { r.summary.textContent = summary; r.summary.dataset.incomplete = incomplete ? 'true' : 'false'; }

    // add to cart
    const available = !!v?.available;
    if (r.addBtn) {
      r.addBtn.disabled = !available || st.busy;
      r.addBtn.dataset.state = st.busy ? 'loading' : '';
      r.addBtn.dataset.mode = incomplete ? 'choose' : 'add';
      if (r.addLabel) r.addLabel.textContent = !v ? 'Choose a color' : !available ? 'Sold out' : incomplete ? 'Choose fragrance' : `Add to cart · ${this.money(saleTotal)}`;
    }
    if (this.#sticky) {
      const sp = this.#sticky.querySelector('[data-acp-sticky-price]');
      const sl = this.#sticky.querySelector('[data-acp-sticky-label]');
      const sb = this.#sticky.querySelector('[data-acp-sticky-btn]');
      if (sp) sp.textContent = incomplete ? `${this.money(saleTotal)} + fragrance` : this.money(saleTotal);
      if (sl) {
        const what = st.mode === 'solo' ? 'diffuser only' : incomplete ? 'choose 1 to 3 fragrances' : `+ ${plural(n, 'fragrance')}`;
        sl.textContent = `${name}${v?.color ? ` · ${v.title}` : ''} · ${what}${st.qty > 1 ? ` × ${st.qty}` : ''}`;
      }
      if (sb) { sb.textContent = !available ? 'Sold out' : incomplete ? 'Choose fragrance' : 'Add to cart'; sb.disabled = !available || st.busy; }
    }
  }
}

if (!customElements.get('aromely-car-pdp')) customElements.define('aromely-car-pdp', AromelyCarPdp);
